import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight,
  Link2,
  LogOut,
  MessagesSquare,
  Newspaper,
  Palette,
  SlidersHorizontal,
  Target,
  Wallet,
} from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';

import { env } from '@/env';
import { useAppSelector } from '@/store/hooks';
import { selectUnreadInboxCount } from '@/store/engagement.slice';
import { useLogtoAuth } from '@/hooks/useLogtoAuth';
import { cn } from '@/lib/cn';

/**
 * L'elenco delle impostazioni, più il logout.
 *
 * Estratto dalla schermata `/settings` perché ora vive in DUE posti: quella
 * schermata e la tasca Impostazioni dello Zaino. Copiarlo avrebbe significato
 * due elenchi che divergono al primo che ne aggiorna uno solo — e il modo in cui
 * si scopre è che una voce esiste in un posto e non nell'altro.
 *
 * Non porta intestazione: il titolo lo mette chi lo ospita, perché nei due posti
 * si chiama in modo diverso ed è a un'altezza diversa della gerarchia.
 */
export function SettingsList({ className }: { className?: string }) {
  const { t } = useTranslation('general');
  const { t: tMissions } = useTranslation('missions');
  const { signOut } = useLogtoAuth();
  const unreadInbox = useAppSelector(selectUnreadInboxCount);

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      <section className="flex flex-col gap-2">
        <SettingsRow
          to="/settings/accounts"
          icon={Link2}
          title={t('social.accounts_title', { defaultValue: 'Accounts' })}
          subtitle={t('social.accounts_row_hint', { defaultValue: 'Games & personal accounts' })}
        />
        <SettingsRow
          to="/settings/communication"
          icon={MessagesSquare}
          title={t('communication', { defaultValue: 'Communication' })}
          subtitle={t('communication_hint', { defaultValue: 'Inbox & chat' })}
          badge={unreadInbox}
        />
        <SettingsRow
          to="/settings/appearance"
          icon={Palette}
          title={t('appearance', { defaultValue: 'Appearance' })}
          subtitle={t('appearanceRowHint', { defaultValue: 'Theme & colours' })}
        />
        <SettingsRow
          to="/news/preferences"
          icon={Newspaper}
          title={t('content', { defaultValue: 'Content' })}
          subtitle={t('contentRowHint', { defaultValue: 'News topics you follow' })}
        />
        <SettingsRow
          to="/settings/preferences"
          icon={SlidersHorizontal}
          title={t('preferences', { defaultValue: 'Preferences' })}
          subtitle={t('preferencesRowHint', { defaultValue: 'Language, haptics, tutorial' })}
        />
        {env.featureQbankEnabled && (
          <SettingsRow
            to="/settings/wallet"
            icon={Wallet}
            title={t('qbank.wallet_levels', { defaultValue: 'Wallet & Levels' })}
            subtitle={t('qbank.wallet_hint', { defaultValue: 'Coins & levels' })}
          />
        )}
        {/* Missioni (Quest) — sotto-sezione del blocco IMPOSTAZIONI, ordine di Luca 2026-08-30.
            Condizionata al flag come gia' fa la riga Wallet qui sopra: quando il flag e' spento la
            riga non c'e', quindi la rotta non e' registrata E non e' raggiungibile da nessuna
            superficie. Le stringhe stanno nel namespace `missions` e non in `general` perche' e'
            li' che vive tutta la copy della sezione — una chiave sola sparsa altrove e' il modo in
            cui i namespace si sfilacciano.

            🔴 RIMESSA QUI il 2026-09-06, e non era qui per un buco del restyle: questo elenco
            NASCE dal Profilo, dove la riga stava, e l'estrazione in `SettingsList` e' partita da
            un ramo del 28/07 che la riga non l'aveva ancora. Senza, `/settings/missions` resta
            registrata in `router.tsx` e **irraggiungibile toccando** — mentre la tasca «Missioni»
            dello Zaino, che invece si tocca, e' un mockup senza `questApi`
            (`components/profile/Missions.tsx:62`). Una vera irraggiungibile e una raggiungibile
            finta: la riga rimette in piedi la prima e non tocca la seconda. */}
        {env.featureMissionsEnabled && (
          <SettingsRow
            to="/settings/missions"
            icon={Target}
            title={tMissions('title')}
            subtitle={tMissions('row_hint')}
          />
        )}
      </section>

      {/* Il logout sta staccato dal resto e in rosso: è l'unica voce di questo
          elenco che non si può annullare tornando indietro. */}
      <button
        type="button"
        onClick={() => void signOut()}
        className="card-edge flex items-center justify-center gap-2 rounded-xl bg-bg-secondary p-3 text-sm font-semibold text-danger transition-opacity active:opacity-80"
      >
        <LogOut className="h-4 w-4" />
        {t('sign_out', { defaultValue: 'Sign out' })}
      </button>
    </div>
  );
}

function SettingsRow({
  to,
  icon: Icon,
  title,
  subtitle,
  badge,
}: {
  to: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  subtitle?: string;
  badge?: number;
}) {
  return (
    <Link
      to={to}
      className="card-edge flex items-center gap-3 rounded-xl bg-bg-secondary p-3 transition-opacity active:opacity-80"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-bg-tertiary text-brand">
        <Icon className="h-4 w-4" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-semibold text-fg">{title}</span>
        {subtitle && <span className="truncate text-xs text-fg-muted">{subtitle}</span>}
      </span>
      {badge != null && badge > 0 && (
        <span
          className={cn(
            'inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full px-1.5',
            'bg-brand text-[11px] font-bold text-brand-fg',
          )}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      <ChevronRight aria-hidden className="h-5 w-5 shrink-0 text-fg-muted" />
    </Link>
  );
}
